import { useState } from 'react'
import {
  Title, Group, Card, Stack, Text, Button, Table, Badge, ActionIcon, Modal,
  LoadingOverlay, Select, Textarea, Pagination, CopyButton, Tooltip,
} from '@mantine/core'
import { IconEye, IconTrash, IconCheck, IconCopy } from '@tabler/icons-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete } from '@/lib/confirm'
import { useRoomBookings } from '@/lib/swr'
import { ROOM_BOOKING_STATUS_CONFIG } from '@/constants'
import type { RoomBooking } from '@/types'

const statusOptions = Object.entries(ROOM_BOOKING_STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))

export default function RoomBookingList() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const params: Record<string, string> = { page: String(page), limit: '20' }
  if (statusFilter) params.status = statusFilter

  const { data, isLoading, mutate } = useRoomBookings(params)
  const bookings: RoomBooking[] = data?.data ?? []
  const pagination = data?.pagination

  const [detailModal, setDetailModal] = useState(false)
  const [selected, setSelected] = useState<RoomBooking | null>(null)
  const [statusModal, setStatusModal] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [saving, setSaving] = useState(false)

  const openDetail = (b: RoomBooking) => { setSelected(b); setDetailModal(true) }

  const openStatusChange = (b: RoomBooking) => {
    setSelected(b)
    setNewStatus(b.status)
    setCancelReason('')
    setStatusModal(true)
  }

  const handleStatusChange = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await api.admin.updateRoomBookingStatus(selected._id, newStatus, cancelReason || undefined)
      toast.success('Статус шинэчлэгдлээ')
      setStatusModal(false)
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    } finally { setSaving(false) }
  }

  const handleDelete = (id: string) => confirmDelete('Энэ захиалгыг устгах уу?', async () => {
    try {
      await api.admin.deleteRoomBooking(id)
      toast.success('Устгагдлаа')
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const getRoomName = (b: RoomBooking) => typeof b.room === 'object' ? b.room.name : b.room
  const formatDate = (d: string) => new Date(d).toLocaleDateString('mn-MN')

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Өрөөний захиалгууд</Title>
        <Select
          placeholder="Бүгд"
          clearable
          w={180}
          size="sm"
          data={statusOptions}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v || ''); setPage(1) }}
        />
      </Group>

      <Card withBorder pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Өвчтөн</Table.Th>
              <Table.Th>Утас</Table.Th>
              <Table.Th>Өрөө</Table.Th>
              <Table.Th>Ирэх</Table.Th>
              <Table.Th>Гарах</Table.Th>
              <Table.Th>Нийт дүн</Table.Th>
              <Table.Th>Мэдээлэл</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th w={100}>Үйлдэл</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {bookings.map((b) => {
              const cfg = ROOM_BOOKING_STATUS_CONFIG[b.status]
              return (
                <Table.Tr key={b._id}>
                  <Table.Td>
                    {b.patientName ? (
                      <>
                        <Text size="sm" fw={600}>{b.patientName}</Text>
                        <Text size="xs" c="dimmed">{b.registerNo}</Text>
                      </>
                    ) : (
                      <Text size="xs" c="dimmed" fs="italic">Бөглөөгүй</Text>
                    )}
                  </Table.Td>
                  <Table.Td><Text size="sm">{b.phone}</Text></Table.Td>
                  <Table.Td><Text size="sm">{getRoomName(b)}</Text></Table.Td>
                  <Table.Td><Text size="sm">{formatDate(b.checkIn)}</Text></Table.Td>
                  <Table.Td><Text size="sm">{formatDate(b.checkOut)}</Text></Table.Td>
                  <Table.Td><Text size="sm">{b.totalAmount.toLocaleString()}₮</Text></Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm" color={b.infoCompleted ? 'green' : 'orange'}>
                      {b.infoCompleted ? '✓ Бүрэн' : '✗ Дутуу'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={cfg?.color || 'gray'} variant="light" size="sm" style={{ cursor: 'pointer' }}
                      onClick={() => openStatusChange(b)}
                    >
                      {cfg?.label || b.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="light" size="sm" onClick={() => openDetail(b)}>
                        <IconEye size={14} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" size="sm" onClick={() => handleDelete(b._id)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              )
            })}
            {bookings.length === 0 && !isLoading && (
              <Table.Tr><Table.Td colSpan={9}><Text ta="center" c="dimmed" py="lg">Захиалга байхгүй байна</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        {pagination && pagination.pages > 1 && (
          <Group justify="center" mt="md">
            <Pagination total={pagination.pages} value={page} onChange={setPage} />
          </Group>
        )}
      </Card>

      {/* Detail modal */}
      <Modal opened={detailModal} onClose={() => setDetailModal(false)} title="Захиалгын дэлгэрэнгүй" size="md">
        {selected && (
          <Stack gap="sm">
            <Group grow>
              <div>
                <Text size="xs" c="dimmed">Өвчтөний нэр</Text>
                <Text size="sm" fw={600}>{selected.patientName || '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Регистр</Text>
                <Text size="sm">{selected.registerNo || '—'}</Text>
              </div>
            </Group>
            <Group grow>
              <div>
                <Text size="xs" c="dimmed">Утас</Text>
                <Text size="sm">{selected.phone}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Өрөө</Text>
                <Text size="sm">{getRoomName(selected)}</Text>
              </div>
            </Group>
            <Group grow>
              <div>
                <Text size="xs" c="dimmed">Ирэх огноо</Text>
                <Text size="sm">{formatDate(selected.checkIn)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Гарах огноо</Text>
                <Text size="sm">{formatDate(selected.checkOut)}</Text>
              </div>
            </Group>
            <Group grow>
              <div>
                <Text size="xs" c="dimmed">Нийт дүн</Text>
                <Text size="sm" fw={600}>{selected.totalAmount.toLocaleString()}₮</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Урьдчилгаа</Text>
                <Text size="sm">{selected.prepaymentAmount.toLocaleString()}₮</Text>
              </div>
            </Group>
            {selected.guardian && (
              <Group grow>
                <div>
                  <Text size="xs" c="dimmed">Асран хамгаалагч</Text>
                  <Text size="sm">{selected.guardian}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Утас</Text>
                  <Text size="sm">{selected.guardianPhone || '—'}</Text>
                </div>
              </Group>
            )}
            <Group grow>
              <div>
                <Text size="xs" c="dimmed">Статус</Text>
                <Badge color={ROOM_BOOKING_STATUS_CONFIG[selected.status]?.color || 'gray'} variant="light">
                  {ROOM_BOOKING_STATUS_CONFIG[selected.status]?.label || selected.status}
                </Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed">Мэдээлэл</Text>
                <Badge color={selected.infoCompleted ? 'green' : 'orange'} variant="light">
                  {selected.infoCompleted ? 'Бүрэн' : 'Дутуу'}
                </Badge>
              </div>
            </Group>
            <div>
              <Text size="xs" c="dimmed" mb={4}>Захиалгын код</Text>
              <Group gap="xs">
                <Text size="sm" ff="monospace" fw={600}>{selected.shortCode}</Text>
                <CopyButton value={`/invoice/${selected.shortCode}`}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Хуулагдлаа' : 'Холбоос хуулах'}>
                      <ActionIcon size="xs" variant="light" color={copied ? 'green' : 'gray'} onClick={copy}>
                        {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </div>
            <Text size="xs" c="dimmed">Огноо: {new Date(selected.createdAt).toLocaleString('mn-MN')}</Text>
          </Stack>
        )}
      </Modal>

      {/* Status change modal */}
      <Modal opened={statusModal} onClose={() => setStatusModal(false)} title="Статус өөрчлөх" size="sm">
        <Stack>
          <Select label="Статус" data={statusOptions} value={newStatus} onChange={(v) => setNewStatus(v || 'PENDING')} />
          {newStatus === 'CANCELED' && (
            <Textarea label="Цуцалсан шалтгаан" value={cancelReason} onChange={(e) => setCancelReason(e.currentTarget.value)} />
          )}
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setStatusModal(false)}>Болих</Button>
            <Button onClick={handleStatusChange} loading={saving}>Хадгалах</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
